import { View } from "@novorender/api";
import pMap from "p-map";
import { useEffect } from "react";

import { useAppStore } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { log } from "utils/log";

import { Picker, selectPicker } from "..";

const l = log.measure.preloadBrep;

/**
 * Minimum visual area (in CSS pixels²) an object must cover to be worth preloading its BREP.
 * Objects below this are too small/far to be likely measure/pick targets, so preloading their
 * (relatively expensive) BREP is not worth the bandwidth.
 *
 * This is DPI-independent because we subsample the pick buffer with a stride of `devicePixelRatio`
 * (see {@link getSampleStride}), so the counts returned by `objectPixelCounts` already approximate
 * CSS-pixel area regardless of display density.
 */
const MIN_PIXEL_COUNT = 20 ** 2;

/**
 * Pick-buffer sampling stride passed as `dx`/`dy` to {@link View.objectPixelCounts}. The pick buffer
 * scales with devicePixelRatio in both dimensions, so sampling every `dpr`-th pixel both keeps the
 * returned counts in approximate CSS-pixel units (DPI-independent threshold) and cuts the work by
 * ~`dpr²` on hi-DPI displays.
 */
function getSampleStride(): number {
    return Math.max(1, Math.round(window.devicePixelRatio || 1));
}

/** Concurrency for BREP downloads. Kept low to avoid competing with rendering/user requests. */
const DOWNLOAD_CONCURRENCY = 3;

/**
 * While the camera stays settled, geometry may still be streaming in. Re-check the
 * rendered triangle count at this interval and re-run the preload if it changed.
 */
const TRIANGLE_POLL_INTERVAL_MS = 2000;

const PRELOAD_PICKERS = [Picker.Measurement, Picker.Manhole, Picker.PointLine, Picker.Coordinates, Picker.Dimension];

/**
 * Preloads BREP data for the objects currently visible on screen while the camera is idle.
 *
 * Triggered on the `cameraSettled` event: it reads per-object pixel coverage via
 * {@link View.objectPixelCounts}, keeps the objects covering at least {@link MIN_PIXEL_COUNT}
 * pixels (sorted by coverage descending), and downloads their BREP with limited concurrency
 * so the data is ready when the user measures/picks.
 *
 * A run is aborted when a new `cameraSettled` arrives, when the camera starts moving again
 * (`cameraUpdated`), or on unmount. Because geometry can keep streaming after the camera settles,
 * the rendered triangle count is polled every {@link TRIANGLE_POLL_INTERVAL_MS} and the preload
 * re-runs whenever it changes; polling also stops on `cameraUpdated`.
 *
 * Already-preloaded objects are tracked so the repeated polling re-runs don't re-request the same
 * BREPs. The BREP cache is bounded, so without this a long-lived camera position could churn the
 * cache and re-download data it just evicted. The tracking set is reset on each `cameraSettled`
 * (new viewpoint => potentially different visible set and a fresh budget).
 *
 * Also if BREPs for current view don't fit into cache - the most significant BREPs (that we preload first)
 * might be evicted from cache by the least significant ones.
 * Not sure how to solve this problem other than
 * - limiting MIN_PIXEL_COUNT
 * - increasing cache size
 * - decreasing BREP size (worth investing I think)
 * - relying on browser cache
 */
export function usePreloadBrep(): void {
    const {
        state: { view, internalEventEmitter },
    } = useExplorerGlobals();
    const store = useAppStore();

    useEffect(() => {
        if (!view || !internalEventEmitter) {
            return;
        }

        let abortController: AbortController | undefined;
        let pollTimer: ReturnType<typeof setInterval> | undefined;
        let lastTriangleCount = -1;
        // Initially assume the camera is settled so the preload runs immediately.
        let isCameraSettled = true;
        // Objects whose BREP we already preloaded for the current camera position, so polling
        // re-runs skip them. Reset on each cameraSettled (see handleCameraSettled).
        let preloaded = new Set<number>();
        let currentPicker = selectPicker(store.getState());

        const runPreload = () => {
            abortController?.abort();
            abortController = new AbortController();
            void preloadVisibleBreps(view, abortController.signal, preloaded);
        };

        const stopPolling = () => {
            if (pollTimer !== undefined) {
                clearInterval(pollTimer);
                pollTimer = undefined;
            }
        };

        const startPolling = () => {
            stopPolling();
            lastTriangleCount = view.statistics?.render.triangles ?? -1;
            pollTimer = setInterval(() => {
                const triangles = view.statistics?.render.triangles ?? -1;
                if (triangles !== lastTriangleCount) {
                    lastTriangleCount = triangles;
                    runPreload();
                }
            }, TRIANGLE_POLL_INTERVAL_MS);
        };

        const handleCameraSettled = () => {
            preloaded = new Set();
            isCameraSettled = true;
            if (!PRELOAD_PICKERS.includes(currentPicker)) {
                return;
            }
            runPreload();
            startPolling();
        };

        const handleCameraUpdated = () => {
            isCameraSettled = false;
            stopPolling();
            // Camera started moving: the visible set is about to change and the user won't be
            // measuring/picking mid-move, so drop the in-flight preload to free up bandwidth.
            abortController?.abort();
            abortController = undefined;
        };

        const handleStoreChange = () => {
            const picker = selectPicker(store.getState());
            if (picker === currentPicker) {
                return;
            }
            currentPicker = picker;
            if (PRELOAD_PICKERS.includes(picker)) {
                if (isCameraSettled) {
                    runPreload();
                    startPolling();
                }
            } else {
                stopPolling();
                abortController?.abort();
                abortController = undefined;
            }
        };

        const unsubscribeSettled = internalEventEmitter.on("cameraSettled", handleCameraSettled);
        const unsubscribeUpdated = internalEventEmitter.on("cameraUpdated", handleCameraUpdated);
        const unsubscribeStore = store.subscribe(handleStoreChange);

        return () => {
            unsubscribeSettled();
            unsubscribeUpdated();
            unsubscribeStore();
            stopPolling();
            abortController?.abort();
        };
    }, [view, internalEventEmitter, store]);
}

async function preloadVisibleBreps(view: View, signal: AbortSignal, preloaded: Set<number>): Promise<void> {
    const core = view.measure?.core;
    if (!core) {
        return;
    }

    try {
        const stride = getSampleStride();
        const start = performance.now();

        // objectPixelCounts may fail because of disposed pick buffer, so retry multiple times
        let pixelCounts = new Map<number, number>();
        const attemptCount = 5;
        for (let i = 0; i < attemptCount; i++) {
            if (signal.aborted) {
                return;
            }
            try {
                pixelCounts = await view.objectPixelCounts({ dx: stride, dy: stride });
                break;
            } catch (error) {
                if (i === attemptCount - 1) {
                    throw error;
                }
            }
        }

        const end = performance.now();
        l.debug("Time taken to get object pixel counts:", end - start, "ms");
        if (signal.aborted) {
            return;
        }

        const objectIds = [...pixelCounts.entries()]
            // Remove objects which are too small on the screen, or already preloaded for this position
            .filter(([objectId, count]) => count >= MIN_PIXEL_COUNT && !preloaded.has(objectId))
            // Objects which are larger on the screen should be preloaded first
            .sort(([, a], [, b]) => b - a)
            .slice(0, 100); // Limit the number of objects to preload to 100 to avoid overwhelming the browser.

        if (objectIds.length === 0) {
            return;
        }

        await pMap(
            objectIds,
            async ([objectId, pixelCount]) => {
                try {
                    l.debug("Preloading BREP for object:", objectId, "with pixel count:", pixelCount);
                    // isParametricDataGenerated is a cheap way to trigger the download of the BREP.
                    // Unlike downloadBrep it doesn't return the BREP data and uses cache.
                    await core.isParametricDataGenerated(objectId);
                    preloaded.add(objectId);
                } catch {
                    // Best-effort preloading; ignore individual download failures.
                }
            },
            { concurrency: DOWNLOAD_CONCURRENCY, signal },
        );
    } catch (error) {
        // Best-effort; pMap rejects with an AbortError when the signal fires
        // (new cameraSettled / unmount). Treat any failure as a no-op.
        if (error instanceof Error && error.name === "AbortError") {
            return;
        }
        console.error("Failed to preload BREP:", error);
    }
}
